import React, { useState, useEffect } from "react";
import TenantDataAreaService from "../../services/tenant/TenantDataAreaService";
import { setDataAreaId, getDataAreaId, clearDataAreaId } from "../../services/dataAreaService";
import UserPreferencesService from "../../services/core/UserPreferencesService";

const DEFAULT_THEME = {
  primaryColor: "#159f91",
  fontPrimaryColor: "#FFFFFF",
  headerFooterColor: "#07111e",
  fontHeaderFooterColor: "#e9f2fb",
};

const applyDataAreaBranding = (branding) => {
  const theme = {
    ...DEFAULT_THEME,
    primaryColor: branding?.primaryColor || DEFAULT_THEME.primaryColor,
    fontPrimaryColor: branding?.fontPrimaryColor || DEFAULT_THEME.fontPrimaryColor,
    headerFooterColor: branding?.headerFooterColor || DEFAULT_THEME.headerFooterColor,
    fontHeaderFooterColor: branding?.fontHeaderFooterColor || DEFAULT_THEME.fontHeaderFooterColor,
  };

  window.dispatchEvent(new CustomEvent("themeUpdated", { detail: theme }));

  if (branding?.logoImage) {
    window.dispatchEvent(new CustomEvent("logoUpdated", {
      detail: { logoUrl: branding.logoImage },
    }));
  } else {
    window.dispatchEvent(new CustomEvent("logoUpdated", { detail: { logoUrl: null } }));
  }
  window.dispatchEvent(new CustomEvent("faviconUpdated", {
    detail: { faviconUrl: branding?.faviconImage || null },
  }));
};

const getStoredUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}");
  } catch (error) {
    return {};
  }
};

const getAllowedDataAreaIds = () => {
  const userData = getStoredUserData();
  const values = Array.isArray(userData?.dataAreas) ? userData.dataAreas : [];
  return values
    .map((value) => String(value || "").trim().toUpperCase())
    .filter(Boolean);
};

const getDataAreasFromMe = () => {
  const userData = getStoredUserData();
  return (Array.isArray(userData?.dataAreas) ? userData.dataAreas : [])
    .map((dataAreaId) => String(dataAreaId || "").trim())
    .filter(Boolean)
    .map((dataAreaId) => ({
      id: dataAreaId,
      dataAreaId,
      name: dataAreaId,
    }));
};

const getCurrentTenantId = () => {
  const userData = getStoredUserData();
  return (
    localStorage.getItem("selectedTenant") ||
    userData?.currentTenantId ||
    userData?.tenantId ||
    localStorage.getItem("tenantId")
  );
};

const filterAllowedDataAreas = (dataAreas) => {
  const allowedDataAreaIds = getAllowedDataAreaIds();
  if (allowedDataAreaIds.length === 0) {
    return dataAreas;
  }
  return dataAreas.filter((dataArea) => {
    const dataAreaId = dataArea?.dataAreaId || dataArea?.id;
    return allowedDataAreaIds.includes(String(dataAreaId || "").toUpperCase());
  });
};

const loadTenantDataAreas = async (tenantId) => {
  try {
    const service = new TenantDataAreaService(tenantId);
    const response = await service.getAll(0, 100);
    let rows = [];
    if (response && response.content) {
      rows = response.content;
    } else if (Array.isArray(response)) {
      rows = response;
    }
    return filterAllowedDataAreas(rows);
  } catch (error) {
    return getDataAreasFromMe();
  }
};

const DataAreaSelector = () => {
  const [dataAreas, setDataAreas] = useState([]);
  const [selectedDataArea, setSelectedDataArea] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDataAreas();

    // Listen for data area reload event
    const handleDataAreasUpdated = () => {
      fetchDataAreas();
    };
    window.addEventListener("dataAreasUpdated", handleDataAreasUpdated);

    const handleUserDataChanged = () => {
      fetchDataAreas();
    };
    window.addEventListener("userDataChanged", handleUserDataChanged);

    // Listen for tenant change to reload data areas
    const handleTenantChanged = (event) => {
      // Clear the selected data area when tenant changes
      clearDataAreaId();
      UserPreferencesService.clearCache();
      setSelectedDataArea(null);
      // Fetch new data areas for the new tenant
      fetchDataAreas();
    };
    window.addEventListener("tenantChanged", handleTenantChanged);

    return () => {
      window.removeEventListener("dataAreasUpdated", handleDataAreasUpdated);
      window.removeEventListener("userDataChanged", handleUserDataChanged);
      window.removeEventListener("tenantChanged", handleTenantChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBrandingForDataArea = async (tenantId, dataAreaId) => {
    if (!tenantId || !dataAreaId) {
      applyDataAreaBranding(null);
      return;
    }

    try {
      const service = new TenantDataAreaService(tenantId);
      const branding = await service.getBranding(dataAreaId);
      applyDataAreaBranding(branding);
    } catch (error) {
      console.error("Error loading data area branding:", error);
      applyDataAreaBranding(null);
    }
  };

  const selectDataArea = (tenantId, dataArea, { persistPreference = false } = {}) => {
    const dataAreaId = dataArea?.dataAreaId || dataArea?.id;
    setSelectedDataArea(dataArea);

    if (dataAreaId) {
      setDataAreaId(String(dataAreaId));
      loadBrandingForDataArea(tenantId, dataAreaId);
      if (persistPreference) {
        UserPreferencesService.rememberDataArea(String(dataAreaId)).catch((error) => {
          console.warn("Could not save data area preference:", error);
        });
      }
    } else {
      clearDataAreaId();
      applyDataAreaBranding(null);
    }
  };

  const fetchDataAreas = async () => {
    try {
      setIsLoading(true);
      
      const tenantId = getCurrentTenantId();
      
      if (!tenantId) {
        // No tenant available, clear data areas
        setDataAreas([]);
        setSelectedDataArea(null);
        setIsLoading(false);
        return;
      }
      
      const allDataAreas = await loadTenantDataAreas(tenantId);

      setDataAreas(allDataAreas);

      const preferences = await UserPreferencesService.get().catch(() => null);
      const preferredDataAreaId =
        preferences?.lastDataAreaId ||
        preferences?.defaultDataAreaId ||
        getDataAreaId();

      const preferredDataArea = preferredDataAreaId
        ? allDataAreas.find(
            (da) => String(da.id).toUpperCase() === String(preferredDataAreaId).toUpperCase()
              || String(da.dataAreaId).toUpperCase() === String(preferredDataAreaId).toUpperCase()
          )
        : null;

      if (preferredDataArea) {
        selectDataArea(tenantId, preferredDataArea);
      } else if (allDataAreas.length > 0) {
        // Preferred data area not found, select first one automatically
        selectDataArea(tenantId, allDataAreas[0], { persistPreference: !preferences?.defaultDataAreaId });
      } else {
        // No data areas available
        clearDataAreaId();
        setSelectedDataArea(null);
        applyDataAreaBranding(null);
      }
    } catch (error) {
      console.error("Error loading data areas:", error);
      setDataAreas([]);
      setSelectedDataArea(null);
      applyDataAreaBranding(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataAreaChange = (dataArea) => {
    const dataAreaId = dataArea?.dataAreaId ?? dataArea?.id ?? dataArea;
    const tenantId = getCurrentTenantId();
    selectDataArea(tenantId, dataArea, { persistPreference: true });
    setIsOpen(false);

    // Dispatch event for components that need to know about data area change
    const event = new CustomEvent("dataAreaChanged", {
      detail: { dataAreaId: dataAreaId },
    });
    window.dispatchEvent(event);
  };

  // Don't render if there's only one data area or still loading
  if (isLoading || dataAreas.length <= 1) {
    return null;
  }

  return (
    <div className="dataarea-selector-dropdown">
      <button
        className="dataarea-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title={selectedDataArea?.name || ""}
      >
        <span className="dataarea-name">
          {selectedDataArea?.name || selectedDataArea?.dataAreaId || selectedDataArea?.id || "DataArea"}
        </span>
        <i
          className={`fa fa-chevron-down dataarea-chevron ${isOpen ? "open" : ""}`}
        ></i>
      </button>

      {isOpen && (
        <>
          <div
            className="dataarea-overlay"
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="dataarea-menu">
            {dataAreas.map((dataArea) => {
              const dataAreaId = dataArea.dataAreaId || dataArea.id;
              return (
                <button
                  key={dataAreaId}
                  className={`dataarea-item ${selectedDataArea?.dataAreaId === dataAreaId || selectedDataArea?.id === dataAreaId ? "active" : ""}`}
                  onClick={() => handleDataAreaChange(dataArea)}
                >
                  <i className="fa fa-building"></i>
                  <span className="dataarea-full-name">
                    {dataArea.name || dataAreaId}
                  </span>
                  {(selectedDataArea?.dataAreaId === dataAreaId || selectedDataArea?.id === dataAreaId) && (
                    <i className="fa fa-check dataarea-check"></i>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DataAreaSelector;
